'use client'

import { AnimatePresence, motion } from 'motion/react'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/button'
import { SlidingPane } from '@/components/sliding-pane'
import { T } from '@/components/t'
import { RestaurantCitySearchFiltersInput } from '@/features/restaurants/restaurants-city-search/restaurant-city-search-filters/restaurant-city-search-filters-input'
import { RestaurantCitySearchFiltersOpenNow } from '@/features/restaurants/restaurants-city-search/restaurant-city-search-filters/restaurant-city-search-filters-open-now'
import { RestaurantCitySearchSorting } from '@/features/restaurants/restaurants-city-search/restaurant-city-search-filters/restaurant-city-search-sorting'
import { RestaurantCitySearchFiltersTagsMobile } from '@/features/restaurants/restaurants-city-search/restaurant-city-search-filters/tags/mobile/restaurant-city-search-filters-tags-mobile'
import { useRestaurantCitySearchContext } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'
import { SvgOutlinedFilter } from '@/icons/svg-outlined-filter'
import { SvgOutlinedSearch } from '@/icons/svg-outlined-search'
import { COLORS } from '@/utils/styles/colors'
import { rgba } from '@/utils/styles/rgba'

export const RestaurantCitySearchFiltersMobile = () => {
    const [isFiltersOpen, setIsFiltersOpen] = useState(false)
    const [isSearchOpen, setIsSearchOpen] = useState(false)

    const [hasInputValue, setHasInputValue] = useState(false)

    const {
        query: { tagSlugs, openNow, name },
    } = useRestaurantCitySearchContext()

    useEffect(() => {
        if (!isSearchOpen && name) {
            setIsSearchOpen(true)
            setHasInputValue(true)
        }
    }, [isSearchOpen, name])

    const onSearchIconClick = useCallback(() => {
        setIsSearchOpen((prevIsOpen) => {
            const nextIsOpen = !prevIsOpen

            // Don't close the search input if there is a value in it
            if (!nextIsOpen && hasInputValue) return prevIsOpen

            return nextIsOpen
        })
    }, [hasInputValue])

    const hasFiltersActive = useMemo(
        () => Boolean(tagSlugs?.length || openNow),
        [tagSlugs, openNow]
    )

    return (
        <Fragment>
            <SlidingPane
                isOpen={isFiltersOpen}
                sizeType="full"
                onClose={() => setIsFiltersOpen(false)}
            >
                <div className="mb-12 px-5">
                    <T
                        h3
                        className="font-semibold text-[16px] mb-4"
                        t="restaurants.city.filterBy"
                    />
                    <RestaurantCitySearchFiltersOpenNow />
                </div>

                <div>
                    <T
                        h3
                        className="font-semibold text-[16px] px-5 mb-4"
                        t="restaurants.city.categories"
                    />

                    <RestaurantCitySearchFiltersTagsMobile />
                </div>
            </SlidingPane>

            <div className="md:hidden flex flex-col items-center mb-4 w-full">
                <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-x-1">
                        <Button
                            backgroundColor={isSearchOpen ? rgba(0, 0, 0, 0.12) : rgba(0, 0, 0, 0)}
                            onClick={onSearchIconClick}
                            styleType="invisible"
                            shape="circle"
                            disabled={hasInputValue && isSearchOpen}
                        >
                            <SvgOutlinedSearch color="black" size={24} />
                        </Button>

                        <Button
                            onClick={() => setIsFiltersOpen((prev) => !prev)}
                            styleType="invisible"
                            shape="circle"
                            className="relative"
                        >
                            <SvgOutlinedFilter
                                color={hasFiltersActive ? COLORS.black : 'black'}
                                size={24}
                            />

                            {hasFiltersActive && (
                                <div
                                    className="rounded-full min-h-3 min-w-3 bg-neotaste absolute top-1.5 right-0.5"
                                    style={{ border: '2px solid rgba(0,0,0,0.05)' }}
                                />
                            )}
                        </Button>
                    </div>

                    <RestaurantCitySearchSorting />
                </div>

                <AnimatePresence initial={false}>
                    {isSearchOpen && (
                        <motion.div
                            key="restaurant-city-search-filters-mobile-search-input"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="w-full overflow-hidden"
                        >
                            {/* Add spacing inside the container with the height-animation, as padding on the animating container is not accounted for in the height animation => UI glitch */}
                            <div className="pt-4" />

                            <RestaurantCitySearchFiltersInput setHasValue={setHasInputValue} />

                            <div className="pt-2" />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </Fragment>
    )
}
